import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Suspense, lazy, useEffect } from "react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { CompanyFilterProvider } from "./contexts/CompanyFilterContext";
import { PermissionsProvider } from "./contexts/PermissionsContext";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import { PortalThemeProvider } from "./contexts/PortalThemeContext";
import { PortalLocationProvider } from "./contexts/PortalLocationContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import PermissionGuard from "./components/PermissionGuard";
import SubscriptionGuard from "./components/SubscriptionGuard";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import DashboardLayout from "./layouts/DashboardLayout";
import LocationPortalLayout from "./layouts/LocationPortalLayout";
import "./App.css";
import "./styles/mobile.css";
import CookieConsentBanner from "./components/CookieConsentBanner";
import { isNative, getPlatform } from "./utils/platform";
import { initErrorCapture } from "./utils/errorCapture";

// Lazy-loaded pages (code splitting)
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const MoneyInOutPage = lazy(() => import("./pages/MoneyInOutPage"));
const IssuesPage = lazy(() => import("./pages/IssuesPage"));
const ExpensesPage = lazy(() => import("./pages/ExpensesPage"));
const LocationsPage = lazy(() => import("./pages/LocationsPage"));
const LocationManagersPage = lazy(() => import("./pages/LocationManagersPage"));
const VendorsPage = lazy(() => import("./pages/VendorsPage"));
const ReferersPage = lazy(() => import("./pages/ReferersPage"));
const ReportsPage = lazy(() => import("./pages/ReportsPage"));
const MachineAccountingReportPage = lazy(() => import("./pages/MachineAccountingReportPage"));
const PartnerPayoutsPage = lazy(() => import("./pages/PartnerPayoutsPage"));
const PartnersPage = lazy(() => import("./pages/PartnersPage"));
const UsersPage = lazy(() => import("./pages/UsersPage"));
const CompaniesPage = lazy(() => import("./pages/CompaniesPage"));
const InventoryPage = lazy(() => import("./pages/InventoryPage"));
const InvoicesPage = lazy(() => import("./pages/InvoicesPage"));
const RoutesPage = lazy(() => import("./pages/RoutesPage"));
const RolePermissionsPage = lazy(() => import("./pages/RolePermissionsPage"));
const SubscriptionPage = lazy(() => import("./pages/SubscriptionPage"));
const SubscriptionSuccessPage = lazy(() => import("./pages/SubscriptionSuccessPage"));
const AccessDeniedPage = lazy(() => import("./pages/AccessDeniedPage"));
const ActivityLogsPage = lazy(() => import("./pages/ActivityLogsPage"));
const GettingStartedPage = lazy(() => import("./pages/GettingStartedPage"));
const HelpCenterPage = lazy(() => import("./pages/HelpCenterPage"));
const PlanManagementPage = lazy(() => import("./pages/PlanManagementPage"));
const ErrorLogsPage = lazy(() => import("./pages/ErrorLogsPage"));
const ProductionMonitorPage = lazy(() => import("./pages/ProductionMonitorPage"));
const VisitorAnalyticsPage = lazy(() => import("./pages/VisitorAnalyticsPage"));
const MFASetupPage = lazy(() => import("./pages/MFASetupPage"));
const AnalyticsDashboardPage = lazy(() => import("./pages/AnalyticsDashboardPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage"));
const CookiePolicyPage = lazy(() => import("./pages/CookiePolicyPage"));
const DataProcessingAgreementPage = lazy(() => import("./pages/DataProcessingAgreementPage"));
const SaasSubscriptionAgreementPage = lazy(() => import("./pages/SaasSubscriptionAgreementPage"));
const LeadsPage = lazy(() => import("./pages/LeadsPage"));
const TaxReportPage = lazy(() => import("./pages/TaxReportPage"));
const TruePnlPage = lazy(() => import("./pages/TruePnlPage"));
const ReferrerPayoutsPage = lazy(() => import("./pages/ReferrerPayoutsPage"));
const AccountingPeriodsPage = lazy(() => import("./pages/AccountingPeriodsPage"));
const AuditTrailPage = lazy(() => import("./pages/AuditTrailPage"));
const CashHandoffsPage = lazy(() => import("./pages/CashHandoffsPage"));
const TransferMoneyPage = lazy(() => import("./pages/TransferMoneyPage"));
const SecurityAuditPage = lazy(() => import("./pages/SecurityAuditPage"));
const SystemStatusPage = lazy(() => import("./pages/SystemStatusPage"));
const ExtraLocationBillingPage = lazy(() => import("./pages/ExtraLocationBillingPage"));
const SignupPage = lazy(() => import("./pages/SignupPage"));
const SignupSuccessPage = lazy(() => import("./pages/SignupSuccessPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
// Portal
const PortalDashboard = lazy(() => import("./pages/portal/PortalDashboard"));
const BookkeepingPage = lazy(() => import("./pages/portal/bookkeeping/index"));
const CreditsPage = lazy(() => import("./pages/portal/CreditsPage"));
const ServiceCallsPage = lazy(() => import("./pages/portal/ServiceCallsPage"));
const PortalReportsPage = lazy(() => import("./pages/portal/ReportsPage"));
const EmployeesPage = lazy(() => import("./pages/portal/EmployeesPage"));
const MyLocationsPage = lazy(() => import("./pages/portal/MyLocationsPage"));
const MyMachinesPage = lazy(() => import("./pages/portal/MyMachinesPage"));
const MyCollectionsPage = lazy(() => import("./pages/portal/MyCollectionsPage"));
const AllLocationsOverviewPage = lazy(() => import("./pages/portal/AllLocationsOverviewPage"));
const UserManagementPage = lazy(() => import("./pages/portal/UserManagementPage"));
const PortalVendorsPage = lazy(() => import("./pages/portal/VendorsPage"));
const PortalPartnersPage = lazy(() => import("./pages/portal/PortalPartnersPage"));

// Combined guard that checks both permission and subscription
const CombinedGuard = ({ page, feature, children }) => (
  <PermissionGuard page={page}>
    <SubscriptionGuard feature={feature || page}>
      {children}
    </SubscriptionGuard>
  </PermissionGuard>
);

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Role-based redirect for location_user and location_employee
  if ((user.role === 'location_user' || user.role === 'location_employee' || user.role === 'partner') && !allowedRoles?.includes('location_user') && !allowedRoles?.includes('location_employee') && !allowedRoles?.includes('partner')) {
    return <Navigate to="/location-portal" replace />;
  }

  // Admin users trying to access portal
  if (!['location_user', 'location_employee', 'partner'].includes(user.role) && (allowedRoles?.includes('location_user') || allowedRoles?.includes('location_employee')) && !allowedRoles?.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  const { user } = useAuth();

  // Determine redirect based on user role
  const getDefaultRedirect = () => {
    if (!user) return '/';
    if (user.role === 'location_user' || user.role === 'location_employee' || user.role === 'partner') return '/location-portal';
    if (user.role === 'location_manager') return '/dashboard';
    return '/dashboard';
  };

  return (
    <Suspense fallback={<div className="flex items-center justify-center h-screen bg-background"><div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" /></div>}>
    <Routes>
      {/* Landing Page - Public */}
      <Route path="/" element={<LandingPage />} />
      
      {/* Login Page */}
      <Route path="/login" element={user ? <Navigate to={getDefaultRedirect()} replace /> : <LoginPage />} />
      
      {/* Forgot Password Page */}
      <Route path="/forgot-password" element={user ? <Navigate to={getDefaultRedirect()} replace /> : <ForgotPasswordPage />} />
      
      {/* Legal Pages - Public */}
      {/* Legal pages (keep as separate routes) */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/cookies" element={<CookiePolicyPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms-of-service" element={<TermsOfServicePage />} />
      <Route path="/data-processing-agreement" element={<DataProcessingAgreementPage />} />
      <Route path="/saas-subscription-agreement" element={<SaasSubscriptionAgreementPage />} />
      
      {/* Blog - Public */}
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      
      {/* Signup Flow - Public */}
      <Route path="/signup" element={user ? <Navigate to={getDefaultRedirect()} replace /> : <SignupPage />} />
      <Route path="/signup/success" element={<SignupSuccessPage />} />
      <Route path="/register" element={<Navigate to="/signup" replace />} />
      
      {/* Location Portal Routes */}
      <Route
        path="/location-portal"
        element={
          <ProtectedRoute allowedRoles={['location_user', 'location_employee', 'partner']}>
            <PortalThemeProvider>
              <PortalLocationProvider>
                <LocationPortalLayout />
              </PortalLocationProvider>
            </PortalThemeProvider>
          </ProtectedRoute>
        }
      >
        <Route index element={<PortalDashboard />} />
        <Route path="bookkeeping" element={<BookkeepingPage />} />
        <Route path="credits" element={<CreditsPage />} />
        <Route path="service-calls" element={<ServiceCallsPage />} />
        <Route path="reports" element={<PortalReportsPage />} />
        <Route path="employees" element={<EmployeesPage />} />
        <Route path="my-locations" element={<MyLocationsPage />} />
        <Route path="my-machines" element={<MyMachinesPage />} />
        <Route path="my-collections" element={<MyCollectionsPage />} />
        <Route path="all-locations" element={<AllLocationsOverviewPage />} />
        <Route path="vendors" element={<PortalVendorsPage />} />
        <Route path="partners" element={<PortalPartnersPage />} />
        <Route path="user-management" element={<UserManagementPage />} />
      </Route>

      {/* Admin Dashboard Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <CompanyFilterProvider>
              <PermissionsProvider>
                <SubscriptionProvider>
                  <DashboardLayout />
                </SubscriptionProvider>
              </PermissionsProvider>
            </CompanyFilterProvider>
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        {/* Basic tier features */}
        <Route path="money-in-out" element={<CombinedGuard page="money_in_out" feature="money_in_out"><MoneyInOutPage /></CombinedGuard>} />
        <Route path="locations" element={<CombinedGuard page="locations" feature="locations"><LocationsPage /></CombinedGuard>} />
        <Route path="referers" element={<CombinedGuard page="referers" feature="referers"><ReferersPage /></CombinedGuard>} />
        <Route path="vendors" element={<CombinedGuard page="vendors" feature="vendors"><VendorsPage /></CombinedGuard>} />
        
        {/* Pro tier features */}
        <Route path="cash-handoffs" element={<CombinedGuard page="money_in_out" feature="money_in_out">{user?.role === 'location_manager' ? <TransferMoneyPage /> : <CashHandoffsPage />}</CombinedGuard>} />
        <Route path="issues" element={<CombinedGuard page="issues" feature="issues"><IssuesPage /></CombinedGuard>} />
        <Route path="expenses" element={<CombinedGuard page="expenses" feature="expenses"><ExpensesPage /></CombinedGuard>} />
        <Route path="location-managers" element={<CombinedGuard page="location_managers" feature="location_managers"><LocationManagersPage /></CombinedGuard>} />
        <Route path="users" element={<CombinedGuard page="users" feature="users"><UsersPage /></CombinedGuard>} />
        <Route path="reports/machine-accounting" element={<CombinedGuard page="machine_accounting" feature="machine_accounting"><MachineAccountingReportPage /></CombinedGuard>} />
        
        {/* Enterprise tier features */}
        <Route path="inventory" element={<CombinedGuard page="inventory" feature="inventory"><InventoryPage /></CombinedGuard>} />
        <Route path="invoices" element={<CombinedGuard page="invoices" feature="invoices"><InvoicesPage /></CombinedGuard>} />
        <Route path="routes" element={<CombinedGuard page="routes" feature="routes"><RoutesPage /></CombinedGuard>} />
        <Route path="partners" element={<CombinedGuard page="partners" feature="partners"><PartnersPage /></CombinedGuard>} />
        <Route path="reports/partner-payouts" element={<CombinedGuard page="partner_payouts" feature="partner_payouts"><PartnerPayoutsPage /></CombinedGuard>} />
        <Route path="companies" element={<CombinedGuard page="companies" feature="companies"><CompaniesPage /></CombinedGuard>} />
        <Route path="role-permissions" element={<CombinedGuard page="role_permissions" feature="role_permissions"><RolePermissionsPage /></CombinedGuard>} />
        <Route path="leads" element={<LeadsPage />} />
        
        {/* Redirects */}
        <Route path="machines" element={<Navigate to="/dashboard/inventory?tab=machines" replace />} />
        <Route path="games" element={<Navigate to="/dashboard/inventory?tab=games" replace />} />
        <Route path="reports" element={<SubscriptionGuard feature="advanced_reports"><ReportsPage /></SubscriptionGuard>} />
        <Route path="reports/accounting-periods" element={<SubscriptionGuard feature="advanced_reports"><AccountingPeriodsPage /></SubscriptionGuard>} />
        <Route path="reports/tax" element={<SubscriptionGuard feature="advanced_reports"><TaxReportPage /></SubscriptionGuard>} />
        <Route path="reports/true-pnl" element={<SubscriptionGuard feature="advanced_reports"><TruePnlPage /></SubscriptionGuard>} />
        <Route path="reports/referrer-payouts" element={<SubscriptionGuard feature="advanced_reports"><ReferrerPayoutsPage /></SubscriptionGuard>} />
        <Route path="audit-trail" element={<AuditTrailPage />} />
        
        {/* Super Admin only - Security Audit */}
        <Route path="security-audit" element={<SecurityAuditPage />} />
        
        {/* No subscription check needed */}
        <Route path="subscription" element={<SubscriptionPage />} />
        <Route path="subscription/success" element={<SubscriptionSuccessPage />} />
        <Route path="access-denied" element={<AccessDeniedPage />} />
        
        {/* Super Admin only - Activity Logs (Enterprise feature) */}
        <Route path="activity-logs" element={
          <SubscriptionGuard feature="activity_logs">
            <ActivityLogsPage />
          </SubscriptionGuard>
        } />
        
        {/* Analytics Dashboard */}
        <Route path="analytics" element={<CombinedGuard page="analytics" feature="analytics"><AnalyticsDashboardPage /></CombinedGuard>} />
        
        {/* Getting Started Guide */}
        <Route path="getting-started" element={<GettingStartedPage />} />
        <Route path="help" element={<HelpCenterPage />} />
        <Route path="plan-management" element={<PlanManagementPage />} />
        <Route path="extra-location-billing" element={<ExtraLocationBillingPage />} />
        <Route path="error-logs" element={<ErrorLogsPage />} />
                <Route path="production-monitor" element={<ProductionMonitorPage />} />
        <Route path="visitor-analytics" element={<VisitorAnalyticsPage />} />
        <Route path="system-status" element={<SystemStatusPage />} />
        
        {/* MFA Setup Page */}
        <Route path="security" element={<MFASetupPage />} />
      </Route>

      <Route path="*" element={<Navigate to={['location_user', 'location_employee', 'partner'].includes(user?.role) ? '/location-portal' : '/dashboard'} replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  // Add platform class to body for mobile-specific CSS
  useEffect(() => {
    if (isNative()) {
      const platform = getPlatform();
      document.body.classList.add('capacitor-native');
      document.body.classList.add(`capacitor-${platform}`);
    }
    initErrorCapture();
  }, []);

  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" />
          <CookieConsentBanner />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
